import { Link, Navigate } from "react-router-dom";
import { FileLock2, CheckCircle2, ShieldCheck, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import Seo from "../components/Seo";

export default function DokumenteTeaserPage() {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (user) return <Navigate to="/meine-dokumente" replace />;

    return (
        <div className="py-12 sm:py-16">
            <Seo
                title="Sichere Dokumentenprüfung mit KI-Voranalyse"
                description="Heimverträge, Bescheide, Pflegegradunterlagen und Rentennachweise sicher hochladen. KI-gestützte Klassifikation, Extraktion von Eigenanteil und Pflegegrad sowie Ampel-Relevanzbewertung. DSGVO-konform."
                path="/dokumente"
            />
            <div className="container max-w-3xl">
                <div className="rounded-3xl border border-brand-200 bg-brand-50/50 p-7 sm:p-10">
                    <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-semibold uppercase tracking-wider text-brand-700">
                        <Sparkles className="h-3.5 w-3.5" /> Jetzt verfügbar
                    </span>
                    <h1 className="mt-4 font-display text-4xl font-bold tracking-tight sm:text-5xl">
                        Sichere Dokumentenprüfung mit KI-Voranalyse
                    </h1>
                    <p className="mt-3 max-w-xl text-ink-500">
                        Laden Sie Heimverträge, Bescheide, Kostenblätter, Rentennachweise und Schreiben vom
                        Sozialamt sicher hoch. Unsere Voranalyse klassifiziert die Dokumente, extrahiert
                        Eckdaten und bewertet die Relevanz per Ampelsystem – für eine deutlich schnellere
                        Beratung.
                    </p>
                    <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                        {[
                            "Automatische Klassifikation (Heimvertrag, Bescheid, …)",
                            "Extraktion von Eigenanteil, Pflegegrad, Daten",
                            "Ampelsystem für Relevanz in der Beratung",
                            "Freigabe an Berater bleibt bei Ihnen",
                        ].map((t) => (
                            <li key={t} className="flex items-start gap-2 text-sm text-ink-700">
                                <CheckCircle2 className="mt-0.5 h-4 w-4 text-brand-600" /> {t}
                            </li>
                        ))}
                    </ul>
                    <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                        <Link to="/register">
                            <Button className="rounded-full bg-brand-500 hover:bg-brand-600" data-testid="doc-cta-register">
                                Kostenlos registrieren &amp; hochladen
                            </Button>
                        </Link>
                        <Link to="/login">
                            <Button variant="outline" className="rounded-full" data-testid="doc-cta-login">
                                Anmelden
                            </Button>
                        </Link>
                    </div>
                </div>

                <div className="mt-8 rounded-3xl border border-ink-300/30 bg-white p-6 text-sm text-ink-700">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand-600">
                        <ShieldCheck className="h-4 w-4" /> DSGVO &amp; Sicherheit
                    </div>
                    <p className="mt-2">
                        Alle Dokumente werden verschlüsselt übertragen und gespeichert. Das Original erhält
                        Ihr Berater ausschließlich, wenn Sie das Dokument explizit freigeben. Sie können jede
                        Freigabe jederzeit widerrufen oder das Dokument löschen.
                    </p>
                </div>

                <div className="mt-8 rounded-3xl border border-brand-200 bg-brand-50/40 p-6">
                    <div className="flex items-start gap-3">
                        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-brand-600">
                            <FileLock2 className="h-5 w-5" />
                        </span>
                        <div>
                            <div className="font-display text-base font-semibold">So einfach geht's</div>
                            <ol className="mt-2 list-decimal pl-4 text-sm text-ink-700">
                                <li>Kostenlos registrieren oder anmelden</li>
                                <li>PDF, JPG oder PNG per Drag &amp; Drop hochladen</li>
                                <li>KI-Voranalyse wird in Sekunden erstellt</li>
                                <li>Auf Wunsch Berater freigeben und Beratung starten</li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
